import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { booleanConverter } from '../common/converters';
import { TeComponent } from '../common/te-component';
import { theme } from '../common/theme';
import { IconName } from '../te-icon';
import '../te-text';
import { TeSegment } from './te-segment';

/** Segment Option Component */
@customElement('te-segment-option')
export class TeSegmentOption extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-segment-wrapper {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 40px;
        cursor: pointer;
        margin: 0px;
        border: none;
        gap: 8px;
        background: transparent;
        z-index: 1;
      }

      .te-segment-wrapper.disabled {
        color: ${theme.colors.shade3};
        pointer-events: none;
      }

      .te-segment-wrapper.active {
        color: ${theme.colors.shade0};
      }

      .te-segment-wrapper te-text {
        max-width: 160px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .text-only te-text {
        padding: 0 32px;
      }

      .text-and-icon te-text {
        padding-right: 32px;
      }

      .text-and-icon te-icon {
        padding-left: 32px;
      }

      .te-segment-wrapper te-text slot {
        transition: color 200ms ease-in-out;
      }
    `,
  ];

  /** Segment option is active and toggled on */
  @property({ type: Boolean, converter: booleanConverter }) active = false;
  /** Segment option is disabled */
  @property({ type: Boolean, converter: booleanConverter }) disabled = false;
  /** Primary text value */
  @property({ type: String }) text?: string;
  /** Icon name from icon pack */
  @property({ type: String }) icon?: IconName;

  handleTextClick(e: Event) {
    if (this.disabled) return;
    this.handleClick(e);
  }

  updated() {
    (this.parentNode as TeSegment).requestUpdate();
  }

  render() {
    const textOnly = this.text && !this.icon;
    const textAndIcon = this.text && this.icon;
    const classes = [
      'te-segment-wrapper',
      this.active ? 'active' : '',
      this.disabled ? 'disabled' : '',
      textAndIcon ? 'text-and-icon' : textOnly ? 'text-only' : '',
    ]
      .filter(Boolean)
      .join(' ');

    const statusColor = this.active
      ? theme.colors.shade0.toString()
      : this.disabled
      ? theme.colors.shade3.toString()
      : '';
    const color = statusColor || theme.colors.text.toString();
    const iconColor = statusColor || theme.colors.shade5.toString();

    return html`
      <button class="${classes}" @click=${this.handleTextClick}>
        ${this.icon
          ? html`
              <te-icon name=${this.icon} size="16" color=${iconColor}></te-icon>
            `
          : null}
        ${this.text
          ? html`
              <te-text weight="700" color=${color}>
                ${this.text}
              </te-text>
            `
          : null}
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-segment-option': TeSegmentOption;
  }
}
